import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthManager } from './auth.service';

// POLAR-751: 2 horas de inactividad real antes de preguntar, 2 minutos de
// gracia desde que se muestra la pregunta antes del cierre automatico de
// seguridad. Refresco silencioso cada 45 min, debajo de la vida util de 1h
// del JWT (ver User.ts loginUserByEmail en users_nodejs). POLAR-875: el
// refresco corre aunque no haya actividad mientras la sesion este dentro del
// limite de inactividad; si solo corriera con actividad, el token de un
// usuario inactivo vencia antes de las 2h y el 401 reactivo de
// authError.interceptor cerraba la sesion a los ~60 min.
const IDLE_THRESHOLD_MS = 2 * 60 * 60 * 1000;
const GRACE_PERIOD_MS = 2 * 60 * 1000;
const REFRESH_INTERVAL_MS = 45 * 60 * 1000;
const TICK_MS = 10 * 1000;

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll'] as const;

@Injectable({ providedIn: 'root' })
export class IdleWatcherService {
  private readonly auth = inject(AuthManager);
  private readonly router = inject(Router);

  public readonly showPrompt = signal(false);

  private lastActivityAt = Date.now();
  private lastRefreshAt = Date.now();
  private graceDeadline: number | null = null;
  private tickHandle?: ReturnType<typeof setInterval>;
  private readonly boundOnActivity = () => this.onActivity();

  start(): void {
    this.lastActivityAt = Date.now();
    this.lastRefreshAt = Date.now();
    this.graceDeadline = null;
    this.showPrompt.set(false);
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, this.boundOnActivity, { passive: true }),
    );
    this.tickHandle = setInterval(() => this.tick(), TICK_MS);
  }

  stop(): void {
    ACTIVITY_EVENTS.forEach((event) =>
      window.removeEventListener(event, this.boundOnActivity),
    );
    if (this.tickHandle) clearInterval(this.tickHandle);
    this.tickHandle = undefined;
  }

  /** El usuario confirmo en el modal que quiere mantener la sesion activa. */
  async confirmKeepAlive(): Promise<void> {
    this.showPrompt.set(false);
    this.graceDeadline = null;
    this.lastActivityAt = Date.now();
    try {
      await this.auth.touchSession();
      this.lastRefreshAt = Date.now();
    } catch (err) {
      console.warn('[IdleWatcherService] touchSession failed on confirm', err);
    }
  }

  /** El usuario decidio explicitamente no mantener la sesion activa. */
  declineKeepAlive(): void {
    this.logout();
  }

  private onActivity(): void {
    this.lastActivityAt = Date.now();
    // Mientras el modal de advertencia esta visible, una interaccion con la
    // pagina de fondo no debe descartarlo en silencio - solo confirmKeepAlive
    // (boton "Si") o el vencimiento de la gracia lo resuelven.
  }

  private tick(): void {
    const now = Date.now();

    if (this.showPrompt()) {
      if (this.graceDeadline !== null && now >= this.graceDeadline) {
        this.logout();
      }
      return;
    }

    const idleFor = now - this.lastActivityAt;
    // POLAR-875: al volver de una suspension (o de una pestana congelada) con
    // el limite ya cumplido, se cierra directamente en vez de abrir el aviso
    // con una gracia nueva.
    if (idleFor >= IDLE_THRESHOLD_MS + GRACE_PERIOD_MS) {
      this.logout();
      return;
    }
    if (idleFor >= IDLE_THRESHOLD_MS) {
      this.graceDeadline = now + GRACE_PERIOD_MS;
      this.showPrompt.set(true);
      return;
    }

    // Renovacion silenciosa cada 45 min mientras la sesion siga dentro del
    // limite de inactividad, haya o no actividad (POLAR-875).
    if (now - this.lastRefreshAt >= REFRESH_INTERVAL_MS) {
      this.lastRefreshAt = now;
      this.auth.touchSession().catch((err) =>
        console.warn('[IdleWatcherService] silent touchSession failed', err),
      );
    }
  }

  private logout(): void {
    this.stop();
    this.showPrompt.set(false);
    this.graceDeadline = null;
    try {
      this.auth.cleanSession();
    } catch (err) {
      console.warn('[IdleWatcherService] logout cleanSession failed', err);
    }
    this.router.navigate(['/login']);
  }
}
